import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  Globe,
  Eye,
  Stethoscope,
  Phone,
  Mail,
  MapPin,
  Shield,
  Zap,
  Plug,
} from 'lucide-react';
import {
  Wordmark,
  Eyebrow,
  INPUT_CLASSES,
  ConsultationForm,
  sendInternalNotification,
} from './shared';

const CLAIMS_VOLUME_OPTIONS = [
  { label: 'Under 200', midpoint: 150, display: '<200' },
  { label: '200 – 500', midpoint: 350, display: '200–500' },
  { label: '500 – 1,000', midpoint: 750, display: '500–1,000' },
  { label: '1,000+', midpoint: 1250, display: '1,000+' },
];

const PATHOLOGY_BENCHMARK_RATE = 92;
const GENERIC_MIN_CHASE_THRESHOLD = 35;
const HORIZON_BILLING_RATE = 4;

const STEP_LABELS = {
  0: 'Email gate',
  1: 'Question 1 — Monthly claims volume',
  2: 'Question 2 — Net collection rate',
  3: 'Question 3 — Average reimbursement',
  4: 'Question 4 — Billing cost',
  5: 'Results viewed',
};

const problemCards = [
  {
    icon: Globe,
    title: 'Offshore Teams & HIPAA Exposure',
    body: 'Many generalist RCM vendors route work overseas. That introduces real HIPAA risk — and pathology billing nuance gets lost in translation when claims are handled by teams unfamiliar with your specialty.',
  },
  {
    icon: Stethoscope,
    title: 'Generalist Billers, Pathology Claims',
    body: 'Pathology has distinct coding rules, modifier requirements, and payer quirks. A biller who handles primary care and orthopedics will miss the details that determine whether a claim pays correctly — or at all.',
  },
  {
    icon: Eye,
    title: 'No Visibility Until the Monthly Report',
    body: 'Most RCM relationships give you a summary at month-end and little else. By then, denials have aged, deadlines have passed, and revenue you could have recovered is already written off.',
  },
];

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function calculateRevenueAtRisk({
  monthlyClaims,
  netCollectionRate,
  avgReimbursement,
  billingCostPercent = 0,
}) {
  const collectionGap = Math.max(0, PATHOLOGY_BENCHMARK_RATE - netCollectionRate) / 100;
  const baseDenialRate = 0.04;
  const denialRate = Math.min(0.18, baseDenialRate + collectionGap * 0.65);

  const monthlyGross = monthlyClaims * avgReimbursement;
  const annualDenialLoss = monthlyGross * denialRate * 12;

  const pctClaimsBelowMinChase =
    avgReimbursement <= GENERIC_MIN_CHASE_THRESHOLD
      ? 0.55
      : Math.min(0.45, (GENERIC_MIN_CHASE_THRESHOLD / avgReimbursement) * 0.35 + 0.12);
  const followUpRate = pctClaimsBelowMinChase * (collectionGap + 0.06);
  const annualFollowUpLoss = monthlyGross * followUpRate * 12;

  let fromBillingOverpay = 0;
  if (billingCostPercent > HORIZON_BILLING_RATE) {
    const annualCollections = monthlyGross * (netCollectionRate / 100) * 12;
    const excessRate = (billingCostPercent - HORIZON_BILLING_RATE) / 100;
    fromBillingOverpay = annualCollections * excessRate;
  }

  const total = annualDenialLoss + annualFollowUpLoss + fromBillingOverpay;

  return {
    total,
    fromDenials: annualDenialLoss,
    fromFollowUp: annualFollowUpLoss,
    fromBillingOverpay,
  };
}

function CalculatorResults({ results, showBreakdown }) {
  return (
    <div className='bg-brand-navy text-brand-cream p-8 sm:p-10'>
      <p
        className='font-body text-[10px] font-semibold uppercase text-brand-cream/60 mb-3'
        style={{ letterSpacing: '0.25em' }}
      >
        Your Estimate
      </p>
      <p className='font-display font-bold text-3xl sm:text-4xl text-brand-cream leading-tight mb-2'>
        Estimated annual revenue at risk:{' '}
        <span className='text-brand-ember'>{formatCurrency(results.total)}</span>
      </p>
      {showBreakdown && results.total > 0 && (
        <div className='mt-4 space-y-2 font-body text-brand-cream/80 text-sm leading-relaxed'>
          <p>
            <span className='text-brand-ember font-semibold'>
              {formatCurrency(results.fromDenials)}
            </span>{' '}
            from denials and coding errors
          </p>
          <p>
            <span className='text-brand-ember font-semibold'>
              {formatCurrency(results.fromFollowUp)}
            </span>{' '}
            from slow or incomplete follow-up
          </p>
          {results.fromBillingOverpay > 0 && (
            <p>
              <span className='text-brand-ember font-semibold'>
                {formatCurrency(results.fromBillingOverpay)}
              </span>{' '}
              from billing fees above Horizon's rate
            </p>
          )}
        </div>
      )}
      <p className='mt-6 font-body text-sm text-brand-cream/70 leading-relaxed'>
        Most billing groups set a minimum dollar amount of around $25 before they'll
        work a claim. At typical pathology reimbursement levels,
        a meaningful share of your claims fall below that threshold and never get
        chased. Horizon RCM has no minimum. We pursue every dollar, because at
        pathology reimbursement levels, that's where the margin actually lives.
      </p>
      <p className='mt-4 font-body text-[11px] text-brand-cream/45 leading-relaxed'>
        Estimate based on industry benchmarks. Your free revenue analysis will use
        your actual numbers.
      </p>
    </div>
  );
}

function CalculatorPage() {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [step, setStep] = useState(0);
  const [claimsVolumeIndex, setClaimsVolumeIndex] = useState(null);
  const [netCollectionRate, setNetCollectionRate] = useState('');
  const [avgReimbursement, setAvgReimbursement] = useState('');
  const [billingCostPercent, setBillingCostPercent] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);

  const leadFormRef = useRef(null);

  const monthlyClaims =
    claimsVolumeIndex !== null ? CLAIMS_VOLUME_OPTIONS[claimsVolumeIndex].midpoint : 0;

  const parsedNetRate = parseFloat(netCollectionRate) || 0;
  const parsedReimbursement = parseFloat(avgReimbursement) || 0;
  const parsedBillingCost = parseFloat(billingCostPercent) || 0;

  const hasEnoughForEstimate =
    claimsVolumeIndex !== null && parsedNetRate > 0 && parsedReimbursement > 0;

  const results = useMemo(() => {
    if (!hasEnoughForEstimate) {
      return { total: 0, fromDenials: 0, fromFollowUp: 0, fromBillingOverpay: 0 };
    }
    return calculateRevenueAtRisk({
      monthlyClaims,
      netCollectionRate: parsedNetRate,
      avgReimbursement: parsedReimbursement,
      billingCostPercent: parsedBillingCost,
    });
  }, [hasEnoughForEstimate, monthlyClaims, parsedNetRate, parsedReimbursement, parsedBillingCost]);

  const claimsVolumeLabel =
    claimsVolumeIndex !== null ? CLAIMS_VOLUME_OPTIONS[claimsVolumeIndex].display : null;

  const calculatorComplete = step >= 5 && hasEnoughForEstimate && parsedBillingCost > 0;

  const buildProgressSource = (action, currentStep = step) => {
    const lines = [
      'Revenue Calculator — activity update',
      `Action: ${action}`,
      `Progress: ${STEP_LABELS[currentStep] || `Step ${currentStep}`}`,
      name && `Name: ${name}`,
      email && `Email: ${email}`,
      claimsVolumeLabel && `Monthly claims: ${claimsVolumeLabel}`,
      parsedNetRate > 0 && `Net collection rate: ${parsedNetRate}%`,
      parsedReimbursement > 0 && `Avg reimbursement: $${parsedReimbursement}`,
      parsedBillingCost > 0 && `Billing cost: ${parsedBillingCost}% of collections`,
      parsedBillingCost > 0 &&
        parsedBillingCost <= HORIZON_BILLING_RATE &&
        `Billing note: at or below Horizon's ${HORIZON_BILLING_RATE}% rate — not included in estimate`,
      parsedBillingCost > HORIZON_BILLING_RATE &&
        `Billing excess vs Horizon (${HORIZON_BILLING_RATE}%): ${(parsedBillingCost - HORIZON_BILLING_RATE).toFixed(1)}%`,
      hasEnoughForEstimate &&
        `Estimated annual revenue at risk: ${formatCurrency(results.total)}`,
    ];
    return lines.filter(Boolean).join('\n');
  };

  const notifyProgress = async (action, currentStep = step) => {
    if (!email.trim() || !isValidEmail(email)) return;
    try {
      await sendInternalNotification({
        fromEmail: email,
        fromName: name,
        source: buildProgressSource(action, currentStep),
      });
    } catch {
      // Non-blocking — don't interrupt the calculator flow
    }
  };

  const formSource = [
    'Revenue calculator — full breakdown request.',
    name && `Name: ${name}`,
    email && `Email: ${email}`,
    claimsVolumeLabel && `Monthly claims: ${claimsVolumeLabel}`,
    parsedNetRate && `Net collection rate: ${parsedNetRate}%`,
    parsedReimbursement && `Avg reimbursement: $${parsedReimbursement}`,
    parsedBillingCost && `Billing cost: ${parsedBillingCost}% of collections`,
    hasEnoughForEstimate && `Estimated annual revenue at risk: ${formatCurrency(results.total)}`,
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') ?? '';

    document.title = 'Revenue Loss Calculator | Horizon RCM';
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'See what generic pathology billing is likely costing your lab. Free revenue loss calculator — estimate denials and follow-up gaps in under a minute.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (metaDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  const handleEmailSubmit = async e => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError('Name is required.');
      return;
    }
    if (!email.trim()) {
      setEmailError('Email address is required.');
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setNameError('');
    setEmailError('');
    setEmailCaptured(true);
    setStep(1);
    await notifyProgress('Started calculator — email submitted', 1);
  };

  const handleNext = async () => {
    if (step < 4) {
      const nextStep = step + 1;
      setStep(nextStep);
      await notifyProgress(`Advanced to question ${nextStep}`, nextStep);
    } else if (step === 4) {
      setStep(5);
      await notifyProgress('Viewed final estimate', 5);
    }
  };

  const handleBack = async () => {
    if (step > 1) {
      const prevStep = step - 1;
      setStep(prevStep);
      await notifyProgress(`Went back to question ${prevStep}`, prevStep);
    }
  };

  const handleCtaClick = async () => {
    setShowLeadForm(true);
    await notifyProgress('Clicked "Get full breakdown" CTA', 5);
    setTimeout(() => {
      leadFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const stepQuestions = [
    {
      number: 1,
      question: 'How many claims does your lab submit each month?',
      content: (
        <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
          {CLAIMS_VOLUME_OPTIONS.map((option, i) => (
            <button
              key={option.label}
              type='button'
              onClick={async () => {
                setClaimsVolumeIndex(i);
                if (step === 1) {
                  setTimeout(async () => {
                    setStep(2);
                    await notifyProgress(
                      `Selected claims volume: ${option.display}`,
                      2
                    );
                  }, 200);
                }
              }}
              className={`px-4 py-4 font-body text-sm font-medium border transition-all duration-200 ${
                claimsVolumeIndex === i
                  ? 'bg-brand-navy text-brand-cream border-brand-navy'
                  : 'bg-brand-warmwhite text-brand-charcoal border-brand-coffee/20 hover:border-brand-ember/50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      ),
    },
    {
      number: 2,
      question: "What's your current net collection rate?",
      hint: 'Enter as a percentage — e.g. 85 for 85%.',
      content: (
        <div className='max-w-xs'>
          <div className='relative'>
            <input
              type='number'
              min='1'
              max='100'
              step='0.1'
              value={netCollectionRate}
              onChange={e => setNetCollectionRate(e.target.value)}
              className={INPUT_CLASSES}
              placeholder='85'
              autoFocus
            />
            <span className='absolute right-4 top-1/2 -translate-y-1/2 font-body text-brand-stone pointer-events-none'>
              %
            </span>
          </div>
        </div>
      ),
      canAdvance: parsedNetRate > 0 && parsedNetRate <= 100,
    },
    {
      number: 3,
      question: "What's your average reimbursement per claim?",
      hint: 'A typical pathology claim averages around $150.',
      content: (
        <div className='max-w-xs'>
          <div className='relative'>
            <span className='absolute left-4 top-1/2 -translate-y-1/2 font-body text-brand-stone pointer-events-none'>
              $
            </span>
            <input
              type='number'
              min='1'
              step='1'
              value={avgReimbursement}
              onChange={e => setAvgReimbursement(e.target.value)}
              className={`${INPUT_CLASSES} pl-8`}
              placeholder='150'
              autoFocus
            />
          </div>
        </div>
      ),
      canAdvance: parsedReimbursement > 0,
    },
    {
      number: 4,
      question: 'What do you currently pay for billing, as a % of collections?',
      hint: 'Include your vendor fee and any per-claim charges.',
      content: (
        <div className='max-w-xs'>
          <div className='relative'>
            <input
              type='number'
              min='0'
              max='30'
              step='0.1'
              value={billingCostPercent}
              onChange={e => setBillingCostPercent(e.target.value)}
              className={INPUT_CLASSES}
              placeholder='6'
              autoFocus
            />
            <span className='absolute right-4 top-1/2 -translate-y-1/2 font-body text-brand-stone pointer-events-none'>
              %
            </span>
          </div>
        </div>
      ),
      canAdvance: parsedBillingCost > 0,
    },
  ];

  const currentQuestion = stepQuestions[step - 1];

  return (
    <div className='min-h-screen bg-brand-cream text-brand-charcoal font-body'>
      <header className='border-b border-brand-coffee/10 bg-brand-warmwhite/95 backdrop-blur-sm'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between gap-6'>
          <Link to='/' className='text-left'>
            <Wordmark />
          </Link>
          <a
            href='tel:+18882576123'
            className='hidden sm:flex items-center gap-2 font-body text-sm font-medium text-brand-coffee hover:text-brand-ember transition-colors'
          >
            <Phone size={16} className='text-brand-ember' />
            (888) 257-6123
          </a>
        </div>
      </header>

      <main>
        {/* Calculator hero */}
        <section className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20'>
          <div className='max-w-3xl mb-12'>
            <Eyebrow className='text-brand-ember mb-4'>Revenue Loss Calculator</Eyebrow>
            <h1 className='font-display font-bold text-brand-coffee text-4xl sm:text-5xl leading-[1.1] mb-6'>
              See what generic billing is likely costing you
            </h1>
            <p className='font-body text-lg text-brand-stone leading-relaxed'>
              Four quick inputs, and you'll see where revenue typically
              leaks with generalist RCM.
            </p>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start'>
            {/* Left: wizard */}
            <div className='lg:col-span-7'>
              <div className='bg-brand-warmwhite border border-brand-coffee/10 p-8 sm:p-10 shadow-sm'>
                {!emailCaptured ? (
                  <>
                    <p className='font-display font-bold text-brand-coffee text-xl sm:text-2xl mb-2'>
                      Tell us where to send the report
                    </p>
                    <form onSubmit={handleEmailSubmit} noValidate>
                      <div className='mb-6'>
                        <label
                          htmlFor='calculator-name'
                          className='block font-body text-sm font-medium text-brand-coffee mb-2'
                        >
                          Name <span className='text-brand-ember'>*</span>
                        </label>
                        <input
                          type='text'
                          id='calculator-name'
                          value={name}
                          onChange={e => {
                            setName(e.target.value);
                            if (nameError) setNameError('');
                          }}
                          className={`${INPUT_CLASSES}${nameError ? ' border-brand-ember ring-2 ring-brand-ember/30' : ''}`}
                          placeholder='Dr. Jane Smith'
                          autoFocus
                        />
                        {nameError && (
                          <p className='mt-2 font-body text-sm text-brand-ember'>{nameError}</p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor='calculator-email'
                          className='block font-body text-sm font-medium text-brand-coffee mb-2'
                        >
                          Email <span className='text-brand-ember'>*</span>
                        </label>
                        <input
                          type='email'
                          id='calculator-email'
                          value={email}
                          onChange={e => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError('');
                          }}
                          className={`${INPUT_CLASSES}${emailError ? ' border-brand-ember ring-2 ring-brand-ember/30' : ''}`}
                          placeholder='you@yourlab.com'
                        />
                        {emailError && (
                          <p className='mt-2 font-body text-sm text-brand-ember'>{emailError}</p>
                        )}
                      </div>
                      <button
                        type='submit'
                        className='mt-6 inline-flex items-center bg-brand-navy hover:bg-brand-coffee text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
                        style={{ letterSpacing: '0.18em' }}
                      >
                        Continue
                        <ChevronRight className='ml-2' size={18} />
                      </button>
                    </form>
                    <p className='mt-6 flex items-center gap-2 font-body text-xs text-brand-stone'>
                      <Shield size={14} className='text-brand-ember flex-shrink-0' />
                      HIPAA-compliant · Your information is never shared or sold
                    </p>
                  </>
                ) : (
                  <>
                    {/* Progress */}
                    <div className='flex items-center gap-2 mb-8'>
                      {stepQuestions.map((q, i) => (
                        <div
                          key={q.number}
                          className={`h-1 flex-1 transition-colors duration-300 ${
                            step > i ? 'bg-brand-ember' : step === i + 1 ? 'bg-brand-ember/60' : 'bg-brand-coffee/10'
                          }`}
                        />
                      ))}
                    </div>

                    {step <= 4 && currentQuestion && (
                      <>
                        <p className='font-body text-xs font-semibold uppercase text-brand-ember mb-3' style={{ letterSpacing: '0.2em' }}>
                          Question {step} of 4
                        </p>
                        <p className='font-display font-bold text-brand-coffee text-xl sm:text-2xl mb-2'>
                          {currentQuestion.question}
                        </p>
                        {currentQuestion.hint && (
                          <p className='font-body text-brand-stone mb-6'>{currentQuestion.hint}</p>
                        )}
                        {!currentQuestion.hint && <div className='mb-6' />}
                        {currentQuestion.content}

                        <div className='mt-8 flex items-center gap-4'>
                          {step > 1 && (
                            <button
                              type='button'
                              onClick={handleBack}
                              className='font-body text-sm text-brand-stone hover:text-brand-coffee transition-colors'
                            >
                              ← Back
                            </button>
                          )}
                          {step !== 1 && (
                            <button
                              type='button'
                              onClick={handleNext}
                              disabled={!currentQuestion.canAdvance}
                              className='inline-flex items-center bg-brand-navy hover:bg-brand-coffee disabled:bg-brand-navy/40 disabled:cursor-not-allowed text-brand-warmwhite font-body font-semibold text-sm uppercase py-3 px-8 transition-colors duration-300'
                              style={{ letterSpacing: '0.18em' }}
                            >
                              {step === 4 ? 'See estimate' : 'Next'}
                              <ChevronRight className='ml-2' size={16} />
                            </button>
                          )}
                        </div>
                      </>
                    )}

                    {calculatorComplete && (
                      <div className='mt-2'>
                        <p className='font-display font-bold text-brand-coffee text-xl sm:text-2xl mb-6'>
                          Here's a reasonable estimate for your lab
                        </p>
                        <button
                          type='button'
                          onClick={handleCtaClick}
                          className='w-full sm:w-auto inline-flex items-center justify-center bg-brand-ember hover:bg-brand-coffee text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
                          style={{ letterSpacing: '0.18em' }}
                        >
                          Get the full breakdown and a plan to recover it
                          <ArrowRight className='ml-2' size={18} />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right: live estimate — always here once enough inputs exist */}
            <div className='lg:col-span-5 lg:sticky lg:top-8'>
              {hasEnoughForEstimate && (
                <CalculatorResults
                  results={results}
                  showBreakdown={step >= 2 || calculatorComplete}
                />
              )}
            </div>
          </div>
        </section>

        {/* Problem section */}
        <section className='bg-brand-navy py-16 sm:py-20'>
          <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='max-w-2xl mb-12'>
              <Eyebrow className='text-brand-ember mb-4'>Why the number adds up</Eyebrow>
              <h2 className='font-display font-bold text-brand-cream text-3xl sm:text-4xl leading-tight mb-4'>
                Generic billing wasn't built for pathology
              </h2>
              <p className='font-body text-brand-cream/70 leading-relaxed'>
                The gaps in your estimate aren't hypothetical — they're the predictable
                result of how most RCM vendors operate. These are the patterns we see
                across pathology labs before they switch.
              </p>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-cream/15'>
              {problemCards.map((card, i) => {
                const Icon = card.icon;
                return (
                  <div key={i} className='bg-brand-navy p-8 sm:p-10'>
                    <Icon className='text-brand-ember mb-5' size={28} strokeWidth={1.5} />
                    <h3 className='font-display font-bold text-brand-cream text-xl mb-3'>
                      {card.title}
                    </h3>
                    <p className='font-body text-brand-cream/70 text-sm leading-relaxed'>
                      {card.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Switching section */}
        <section className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-12 items-center'>
            <div>
              <Eyebrow className='text-brand-ember mb-4'>Easy to Switch</Eyebrow>
              <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl leading-tight mb-6'>
                No startup costs. No disruption.
              </h2>
              <p className='font-body text-brand-stone leading-relaxed mb-6'>
                Switching billing vendors sounds harder than it is. Horizon RCM has
                zero startup fees, and we're already integrated with most major
                laboratory information systems — so the transition is straightforward,
                not a six-month project.
              </p>
              <p className='font-body text-brand-stone leading-relaxed'>
                We handle the onboarding, map your existing workflows, and go live
                without interrupting your lab's operations. Most groups are fully
                transitioned within weeks, not quarters.
              </p>
            </div>
            <div className='space-y-6'>
              <div className='flex items-start gap-4 bg-brand-warmwhite border border-brand-coffee/10 p-6'>
                <Zap className='text-brand-ember flex-shrink-0 mt-0.5' size={22} />
                <div>
                  <h3 className='font-display font-bold text-brand-coffee text-lg mb-1'>
                    Zero startup costs
                  </h3>
                  <p className='font-body text-sm text-brand-stone leading-relaxed'>
                    Competitive pricing with no hidden fees. You pay for performance,
                    not onboarding.
                  </p>
                </div>
              </div>
              <div className='flex items-start gap-4 bg-brand-warmwhite border border-brand-coffee/10 p-6'>
                <Plug className='text-brand-ember flex-shrink-0 mt-0.5' size={22} />
                <div>
                  <h3 className='font-display font-bold text-brand-coffee text-lg mb-1'>
                    Integrated with your LIS
                  </h3>
                  <p className='font-body text-sm text-brand-stone leading-relaxed'>
                    We connect with most laboratory information systems, so data flows
                    without manual re-entry or duplicate work.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact form */}
        <section
          ref={leadFormRef}
          id='contact'
          className='bg-brand-warmwhite border-t border-brand-coffee/10 py-16 sm:py-20 scroll-mt-8'
        >
          <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 lg:grid-cols-12 gap-12 items-start'>
              <div className='lg:col-span-5'>
                <Eyebrow className='text-brand-ember mb-4'>Next Step</Eyebrow>
                <h2 className='font-display font-bold text-brand-coffee text-3xl sm:text-4xl leading-tight mb-6'>
                  Get your full breakdown
                </h2>
                <p className='font-body text-brand-stone leading-relaxed mb-6'>
                  {claimsVolumeLabel
                    ? `Based on your estimate (${claimsVolumeLabel} claims/month), we'll put together a detailed revenue analysis using your actual payer mix and denial history.`
                    : "We'll put together a detailed revenue analysis using your actual payer mix and denial history — not benchmarks."}
                </p>
                <div className='space-y-4'>
                  <a
                    href='mailto:main@horizon-rcm.com'
                    className='inline-flex items-center gap-2 font-body text-sm text-brand-coffee hover:text-brand-ember transition-colors'
                  >
                    <Mail size={16} className='text-brand-ember' />
                    main@horizon-rcm.com
                  </a>
                  <a
                    href='tel:+18882576123'
                    className='flex items-center gap-2 font-body text-sm text-brand-coffee hover:text-brand-ember transition-colors'
                  >
                    <Phone size={16} className='text-brand-ember' />
                    (888) 257-6123
                  </a>
                </div>
              </div>

              <div className='lg:col-span-7'>
                <div
                  className={`bg-brand-cream border border-brand-coffee/10 p-8 sm:p-12 shadow-sm transition-all duration-500 ${
                    showLeadForm ? 'ring-2 ring-brand-ember/30' : ''
                  }`}
                >
                  <ConsultationForm
                    idPrefix='calculator'
                    showNameField
                    initialName={name}
                    initialEmail={email}
                    source={formSource}
                    title='Request Your Free Revenue Analysis'
                    description="Confirm your details and we'll follow up with a personalized breakdown and recovery plan."
                    submitLabel='Get My Full Breakdown'
                    skipConfirmation
                    successMessage="Thank you! We'll be in touch shortly with your personalized breakdown."
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className='bg-brand-navy py-10 border-t-4 border-brand-ember'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6'>
          <Wordmark onDark />
          <div className='text-center sm:text-right'>
            <Link
              to='/'
              className='font-body text-sm text-brand-cream/70 hover:text-brand-ember transition-colors'
            >
              Explore the full site →
            </Link>
            <p className='font-body text-brand-cream/40 text-xs mt-3 flex items-center justify-center sm:justify-end gap-1.5'>
              <MapPin size={12} />
              100% US-Based · Exclusive Pathology Billing
            </p>
          </div>
        </div>
        <p className='font-body text-brand-cream/40 text-xs text-center mt-8'>
          © 2025 Horizon RCM Partners LLC. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default CalculatorPage;
